import { DataServiceInterface } from '@/services/interface/DataService.js'

import store from '@/store'

// dashboard
import economicEfficiency from '@/store/modules/demo/dashboard/economic_efficiency.json'
import criticalIndicators from '@/store/modules/demo/dashboard/critical_indicators.json'
import tendersCount from '@/store/modules/demo/dashboard/tenders_count.json'
import tendersSum from '@/store/modules/demo/dashboard/tenders_sum.json'
import offersCount from '@/store/modules/demo/dashboard/offers_count.json'
import tendersAvgTimeSpending from '@/store/modules/demo/dashboard/tenders_avg_time_spending.json'
import tendersAvgTimeDuration from '@/store/modules/demo/dashboard/tenders_avg_time_duration.json'
import countCompaniesInvitedByOrg from '@/store/modules/demo/dashboard/get_count_companies_invited_by_org.json'
import countCompaniesInvitedByTpro from '@/store/modules/demo/dashboard/count_companies_invited_by_tpro.json'
import providersTop10 from '@/store/modules/demo/dashboard/providers_top_10.json'

// entities
import claims from '@/store/modules/demo/claims.json'
import claimsPositions from '@/store/modules/demo/claims_positions.json'
import claimsComments from '@/store/modules/demo/claims_comments.json'
import procedures from '@/store/modules/demo/procedures.json'
import contracts from '@/store/modules/demo/contracts.json'
import contractStatuses from '@/store/modules/demo/contract_statuses.json'
import orders from '@/store/modules/demo/orders.json'
import orderStatuses from '@/store/modules/demo/order_statuses.json'
import users from '@/store/modules/demo/users.json'
import companies from '@/store/modules/demo/companies.json'
import products from '@/store/modules/demo/products.json'

import moment from 'moment'
import lodash from 'lodash'
import { momentFromString } from '@/utils/datetime.js'

export class DemoDataService extends DataServiceInterface {
  static async initialLoad (params) {
    await store.dispatch('companyInfo/loadData')
    await store.dispatch('product/load')
    await store.dispatch('claim/load')
    await store.dispatch('procedure/load')
    await store.dispatch('contract/loadStatuses')
    await store.dispatch('order/loadStatuses')
    await store.dispatch('dashboard/get_data', { from: '2020-01-01', to: '2020-05-31', periodType: 'month' })
    return Promise.resolve({})
  }

  static async getMessages () {
    return null
  }

  static async getEconomicEfficiency (params) {
    return Promise.resolve({
      data: {
        indicators: {
          economicEfficiency: economicEfficiency
        }
      }
    })
  }

  static async getTendersCount (params) {
    return Promise.resolve({
      data: {
        indicators: {
          tendersCount: tendersCount
        }
      }
    })
  }

  static async getTendersSum (params) {
    return Promise.resolve({
      data: {
        indicators: {
          tendersSum: tendersSum
        }
      }
    })
  }

  static async getOffersCount (params) {
    return Promise.resolve({
      data: {
        indicators: {
          offersCount: offersCount
        }
      }
    })
  }

  static async getTendersAvgTimeSpending (params) {
    return Promise.resolve({
      data: {
        indicators: {
          tendersAvgTimeSpending: tendersAvgTimeSpending
        }
      }
    })
  }

  static async getTendersAvgTimeDuration (params) {
    return Promise.resolve({
      data: {
        indicators: {
          tendersAvgTimeDuration: tendersAvgTimeDuration
        }
      }
    })
  }

  static async getCountCompaniesInvitedByOrg (params) {
    return Promise.resolve({
      data: {
        indicators: {
          countCompaniesInvitedByOrg: countCompaniesInvitedByOrg
        }
      }
    })
  }

  static async getCountCompaniesInvitedByTpro (params) {
    return Promise.resolve({
      data: {
        indicators: {
          countCompaniesInvitedByTpro: countCompaniesInvitedByTpro
        }
      }
    })
  }

  static async getProvidersTop10 (params) {
    return Promise.resolve({
      data: {
        indicators: {
          providersTop10: providersTop10
        }
      }
    })
  }

  static async getCriticalIndicators (params) {
    return Promise.resolve({
      data: {
        indicators: {
          criticalIndicators: criticalIndicators
        }
      }
    })
  }

  static async getClaims (params) {
    const filtered = claims
      .filter(c => 'from' in params ? momentFromString(c.createDate).isBetween(momentFromString(params.from), momentFromString(params.to)) : true)
      .filter(c => 'departmentId' in params ? c.departmentId === params.departmentId : true)
      .filter(c => 'employeeId' in params ? c.userId === params.employeeId : true)
      .filter(c => 'claimId' in params ? c.id === params.claimId : true)
      .filter(c => 'critical' in params ? c.critical === params.critical : true)
      .filter(c => 'states' in params ? params.states.map(s => +s).includes(store.getters['claim/statuses'].find(s => s.name === c.state)?.id) : true)
    return Promise.resolve({ data: { claims: filtered } })
  }

  static async getClaimsStats (params) {
    const stats = claims.filter((claim) => {
      return 'departmentId' in params ? claim.departmentId === params.departmentId : true
    }).filter((claim) => {
      return 'employeeId' in params ? claim.userId === params.employeeId : true
    }).filter((claim) => {
      return 'critical' in params ? claim.critical === 1 : true
    }).filter((claim) => {
      return ('from' in params && 'to' in params) ? moment(claim.createDate).isBetween(moment(params.from), moment(params.to)) : true
    }).reduce((acc, claim) => {
      acc[claim.state] = {
        group: claim.state,
        count: 1 + (acc[claim.state] ? acc[claim.state].count : 0)
      }
      return acc
    }, {})
    return Promise.resolve({ data: { claims_stats: Object.values(stats) } })
  }

  static async getClaimDetails ({ claimId }) {
    return Promise.resolve({
      data: {
        claimPositions: claimsPositions.filter(cp => cp.claimId === claimId),
        claimComments: claimsComments.filter(cc => cc.claimId === claimId)
      }
    })
  }

  static async getClaimsLogs (params) {
    const logs = claims.reduce((acc, claim) => { return acc.concat(claim.logs) }, [])
      .filter(c => 'from' in params ? momentFromString(c.date).isBetween(momentFromString(params.from), momentFromString(params.to)) : true)
      .filter(c => 'departmentId' in params ? c.departmentId === params.departmentId : true)
      .filter(c => 'employeeId' in params ? c.userId === params.employeeId : true)
      .filter(c => 'claimId' in params ? c.claimId === params.claimId : true)
      .filter(c => 'critical' in params ? c.critical === params.critical : true)
      .filter(c => 'states' in params ? params.states.map(s => +s).includes(store.getters['claim/statuses'].find(s => s.name === c.state)?.id) : true)
      .slice(params.offset, params.limit)

    return Promise.resolve({
      data: {
        claim_logs: logs
      }
    })
  }

  static async getProcedures (params) {
    const filtered = procedures
      .filter(p => 'employeeId' in params ? p.managerId === params.employeeId : true)
      .filter(p => 'procedureId' in params ? p.id === params.procedureId : true)
      .filter(p => 'states' in params ? params.states.includes(p.stateId) : true)
      .filter(p => 'type' in params ? p.type === params.type : true)
    return Promise.resolve({ data: { procedures: filtered } })
  }

  static async getProceduresStats (params) {
    const stats = procedures.filter((proc) => {
      return 'departmentId' in params ? proc.departmentId === params.departmentId : true
    }).filter((proc) => {
      return 'employeeId' in params ? proc.managerId === params.employeeId : true
    }).filter((proc) => {
      return 'type' in params ? proc.type === params.type : true
    }).reduce((acc, proc) => {
      acc[proc.stateId] = {
        group: proc.stateId,
        count: 1 + (acc[proc.stateId] ? acc[proc.stateId].count : 0)
      }
      return acc
    }, {})
    return Promise.resolve({ data: { procedures_stats: Object.values(stats) } })
  }

  static async getProcedureDetails (params) {
    return Promise.resolve({ data: { procedure: procedures.filter(proc => proc.id === params.procId)[0] } })
  }

  static async getProcedureWinners (params) {
    // TODO
    return Promise.resolve({ data: { procedure_winner: [] } })
  }

  static async getCompanies (companiesIds) {
    // TODO: implement backend api
    const ids = new Set(companiesIds)
    return Promise.resolve({
      data: {
        companies: companies.filter(u => ids.has(u.id))
      }
    })
  }

  static async getManagers ({ companyId }) {
    const managers = users.reduce((acc, u) => {
      acc[u.id] = { id: u.id, name: u.fullName }
      return acc
    }, {})
    return Promise.resolve({ data: { managers: Object.values(managers) } })
  }

  static async getDepartments ({ companyId }) {
    const departments = claims.reduce((acc, u) => {
      if (u.departmentId) {
        acc[u.departmentId] = { id: u.departmentId, name: u.departmentName }
      }
      return acc
    }, {})
    return Promise.resolve({ data: { departments: Object.values(departments) } })
  }

  static async getContractState (params) {
    return Promise.resolve({
      fields: getContractStateFields(contracts.find((c) => c.id === params.id))
    })
  }

  static async getContracts (params) {
    var filtered = contracts
      .filter(c => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && c.company_id === 1000) || (params.filter.direction === 'IN' && c.company_id !== 1000)
        : true)
      .filter(s => params.filter?.direction === 'IN'
        ? s.status_code !== 'draft'
        : true)
      .filter(c => lodash.has(params, 'filter.status_code')
        ? lodash.includes(params.filter.status_code, c.status_code)
        : true)
      .filter(c => lodash.has(params, 'filter.search')
        ? lodash.includes(c.title, params.filter.search) || lodash.includes(c.number, params.filter.search)
        : true)
      .filter(c => lodash.has(params, 'filter.procedure')
        ? c.tender_id === params.filter.procedure
        : true)

    if (lodash.has(params, 'sort')) {
      filtered = lodash.orderBy(filtered, (c) => moment(c.start_at).unix(), params.sort.toLowerCase())
    }

    const totalCount = (filtered.lenght || [])
    filtered = lodash.slice(filtered, params.offset, params.offset + params.limit)

    return Promise.resolve({ data: { contract: { data: filtered, total_count: totalCount } } })
  }

  static async getContractStatuses (params) {
    return Promise.resolve({ data: { contract_status: contractStatuses } })
  }

  static async getReference (params) {
    console.log('getReference', params)

    const e = params.entity === 'contract'
      ? contracts.find((c) => c.id === params.id)
      : orders.find((c) => c.id === params.id)

    switch (params.reference) {
      case 'counteragent_company_id':
        return Promise.resolve({
          data: {
            reference: { errors: null, possible_values: null, spec: null, type: 'StringField', value: companies.find((c) => c.id === e.counteragent_company_id).name }
          }
        })
      case 'counteragent_user_id':
        return Promise.resolve({
          data: {
            reference: { errors: null, possible_values: null, spec: null, type: 'StringField', value: users.find((u) => u.id === e.counteragent_user_id).fullName }
          }
        })
      case 'tender_id':
        return Promise.resolve({
          data: {
            reference: { errors: null, possible_values: null, spec: null, type: 'StringField', value: procedures.find((p) => p.id === e.tender_id).title }
          }
        })
      case 'currency_id':
        return Promise.resolve({
          data: {
            reference: { errors: null, possible_values: null, spec: null, type: 'StringField', value: 'руб' }
          }
        })
      case 'contract_id':
        return Promise.resolve({
          data: {
            reference: { errors: null, possible_values: null, spec: null, type: 'StringField', value: contracts.find((c) => c.id === e.contract_id).title }
          }
        })
      default:
        return Promise.resolve({})
    }
  }

  static async getContractLogs (params) {
    var filtered = getLogs(contracts, contractStatuses)
      .filter(s => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && s.companyId === 1000) || (params.filter.direction === 'IN' && s.companyId !== 1000)
        : true)
      .filter(s => lodash.has(params, 'filter.status_code')
        ? lodash.includes(params.filter.status_code, s.newStatus)
        : true)
      .filter(s => params.filter?.direction === 'IN'
        ? s.newStatus !== 'draft'
        : true)
      .filter(s => lodash.has(params, 'filter.contract_id')
        ? s.contractId === params.filter.contract_id
        : true)
      .filter(s => lodash.has(params, 'filter.contractId')
        ? s.contractId === params.filter.contractId
        : true)

    if (lodash.has(params, 'sort')) {
      filtered = lodash.orderBy(filtered, (s) => moment(s.insertedAt).unix(), params.sort.toLowerCase())
    }

    const totalCount = (filtered.lenght || [])
    if (lodash.has(params, 'offset') && lodash.has(params, 'limit')) {
      filtered = lodash.slice(filtered, params.offset, params.offset + params.limit)
    }

    return Promise.resolve({ data: { contract_logs: { items: filtered, total_count: totalCount } } })
  }

  static async getContractLogsStats (params) {
    var filtered = getLogs(contracts, contractStatuses)
      .filter(s => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && s.companyId === 1000) || (params.filter.direction === 'IN' && s.companyId !== 1000)
        : true)
      .filter(s => params.filter?.direction === 'IN'
        ? s.newStatus !== 'draft'
        : true)
      .filter(s => lodash.has(params, 'filter.contract_id')
        ? s.contractId === params.filter.contract_id
        : true)
      .filter(s => lodash.has(params, 'filter.contractId')
        ? s.contractId === params.filter.contractId
        : true)

    const stats = filtered.reduce((acc, s) => {
      acc[s.newStatus] = (acc[s.newStatus] || 0) + 1
      return acc
    }, {})
    return Promise.resolve({
      data: {
        contract_logs_stats: lodash.transform(stats, (result, value, key) => {
          result.push({ group: key, count: value })
        }, [])
      }
    })
  }

  static async getContractStats (params) {
    var filtered = contracts
      .filter(c => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && c.company_id === 1000) || (params.filter.direction === 'IN' && c.company_id !== 1000)
        : true)
      .filter(c => lodash.has(params, 'filter.procedure')
        ? c.tender_id === params.filter.procedure
        : true)

    const stats = filtered.reduce((acc, c) => {
      acc[c.status_code] = (acc[c.status_code] || 0) + 1
      return acc
    }, {})
    return Promise.resolve({
      data: {
        contract_stats: lodash.transform(stats, (result, value, key) => {
          result.push({ group: key, count: value })
        }, [])
      }
    })
  }

  static async getContractPositions (params) {
    return Promise.resolve({ positions: [], totalCount: 0, actions: { create: false, read: true, update: false, delete: false } })
  }

  static async getOrderState (params) {
    return Promise.resolve({
      fields: getOrderStateFields(orders.find((o) => o.id === params.id))
    })
  }

  static async getOrders (params) {
    var filtered = orders
      .filter(o => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && o.company_id === 1000) || (params.filter.direction === 'IN' && o.company_id !== 1000)
        : true)
      .filter(s => params.filter?.direction === 'IN'
        ? s.status_code !== 'draft'
        : true)
      .filter(o => lodash.has(params, 'filter.status_code')
        ? lodash.includes(params.filter.status_code, o.status_code)
        : true)
      .filter(o => lodash.has(params, 'filter.search')
        ? lodash.includes(o.title, params.filter.search) || lodash.includes(o.number, params.filter.search)
        : true)
      .filter(o => lodash.has(params, 'filter.contract')
        ? o.contract_id === params.filter.contract
        : true)

    if (lodash.has(params, 'sort')) {
      filtered = lodash.orderBy(filtered, (o) => moment(o.start_at).unix(), params.sort.toLowerCase())
    }

    const totalCount = (filtered.lenght || [])
    filtered = lodash.slice(filtered, params.offset, params.offset + params.limit)

    return Promise.resolve({ data: { order: { data: filtered, total_count: totalCount } } })
  }

  static async getOrderStatuses (params) {
    return Promise.resolve({ data: { order_status: orderStatuses } })
  }

  static async getOrderLogs (params) {
    var filtered = getLogs(orders, orderStatuses)
      .filter(s => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && s.companyId === 1000) || (params.filter.direction === 'IN' && s.companyId !== 1000)
        : true)
      .filter(s => params.filter?.direction === 'IN'
        ? s.newStatus !== 'draft'
        : true)
      .filter(s => lodash.has(params, 'filter.status_code')
        ? lodash.includes(params.filter.status_code, s.newStatus)
        : true)
      .filter(s => lodash.has(params, 'filter.order_id')
        ? s.orderId === params.filter.order_id
        : true)
      .filter(s => lodash.has(params, 'filter.orderId')
        ? s.orderId === params.filter.orderId
        : true)

    if (lodash.has(params, 'sort')) {
      filtered = lodash.orderBy(filtered, (s) => moment(s.insertedAt).unix(), params.sort.toLowerCase())
    }

    const totalCount = (filtered.lenght || [])
    if (lodash.has(params, 'offset') && lodash.has(params, 'limit')) {
      filtered = lodash.slice(filtered, params.offset, params.offset + params.limit)
    }

    return Promise.resolve({ data: { order_logs: { items: filtered, total_count: totalCount } } })
  }

  static async getOrderLogsStats (params) {
    var filtered = getLogs(orders, orderStatuses)
      .filter(s => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && s.companyId === 1000) || (params.filter.direction === 'IN' && s.companyId !== 1000)
        : true)
      .filter(s => params.filter?.direction === 'IN'
        ? s.newStatus !== 'draft'
        : true)
      .filter(s => lodash.has(params, 'filter.order_id')
        ? s.orderId === params.filter.order_id
        : true)
      .filter(s => lodash.has(params, 'filter.orderId')
        ? s.orderId === params.filter.orderId
        : true)

    const stats = filtered.reduce((acc, s) => {
      acc[s.newStatus] = (acc[s.newStatus] || 0) + 1
      return acc
    }, {})
    return Promise.resolve({
      data: {
        order_logs_stats: lodash.transform(stats, (result, value, key) => {
          result.push({ group: key, count: value })
        }, [])
      }
    })
  }

  static async getOrderStats (params) {
    var filtered = orders
      .filter(o => lodash.has(params, 'filter.direction')
        ? (params.filter.direction === 'OUT' && o.company_id === 1000) || (params.filter.direction === 'IN' && o.company_id !== 1000)
        : true)
      .filter(o => lodash.has(params, 'filter.contract')
        ? o.contract_id === params.filter.contract
        : true)

    const stats = filtered.reduce((acc, o) => {
      acc[o.status_code] = (acc[o.status_code] || 0) + 1
      return acc
    }, {})
    return Promise.resolve({
      data: {
        order_stats: lodash.transform(stats, (result, value, key) => {
          result.push({ group: key, count: value })
        }, [])
      }
    })
  }

  static async getOrderPositions (params) {
    return Promise.resolve({ positions: [], totalCount: 0, actions: { create: false, read: true, update: false, delete: false } })
  }

  static async getUsers (usersIds) {
    // TODO: implement backend api
    const ids = new Set(usersIds)
    return Promise.resolve({
      data: {
        users: users.filter(u => ids.has(u.id))
      }
    })
  }

  static async getProducts (params) {
    return Promise.resolve({
      data: {
        products: products
      }
    })
  }

  static async getUnits (params) {
    return Promise.resolve({
      data: {
        units: []
      }
    })
  }

  static async getCurrencies (params) {
    return Promise.resolve({
      data: {
        currencies: []
      }
    })
  }

  /*
    entityCode: код сущности для которой надо отобразить сквозную связку = 'claim' | 'procedure' | 'contract' | 'code'
    entityId: идентификатор сущности
  */
  static async getCrossEntitiesRelations ({ entityCode, entityId }) {
    function findParents (stack, child, acc) {
      if (child && stack.length > 0) {
        const { code, items } = stack.pop()
        const parent = items.find(e => e.id === child.inherit_proc_id)
        if (parent) {
          acc[code] = [parent.id]
        }
        if (stack.length > 0) {
          return findParents(stack, parent, acc)
        }
      }
      return acc
    }

    function findChildren (stack, parent, acc) {
      if (parent && stack.length > 0) {
        const { code, items } = stack.shift()
        const children = items.filter(e => e.inherit_proc_id === parent.id)
        if (children.length > 0) {
          acc[code] = Array.isArray(acc[code])
            ? acc[code].concat(children.map(c => c.id))
            : children.map(c => c.id)
        }
        if (stack.length > 0) {
          return children.reduce((acc, child) => {
            return findChildren(stack.slice(), child, acc)
          }, acc)
        }
      }
      return acc
    }

    function splitEntitiesStack (stack, entityCode, id) {
      const etyIndex = stack.findIndex(e => e.code === entityCode)
      const parentStack = stack.slice(0, etyIndex)
      const childrenStack = stack.slice(etyIndex + 1, stack.length)
      const entity = stack[etyIndex].items.find(e => e.id === id)
      return { parentStack, childrenStack, entity }
    }

    const entitiesStack = [{
      code: 'claim', items: claims
    }, {
      code: 'procedure', items: procedures
    }, {
      code: 'contract', items: contracts
    }, {
      code: 'order', items: orders
    }]

    const {
      parentStack,
      childrenStack,
      entity
    } = splitEntitiesStack(entitiesStack, entityCode, entityId)

    var relations = {
      ...findParents(parentStack, entity, {}),
      ...findChildren(childrenStack, entity, {})
    }
    relations[entityCode] = [entity.id]

    // Для эмуляции долгой загрузки
    // function sleep (ms) {
    //  return new Promise(resolve => setTimeout(resolve, ms))
    // }
    // await sleep(3000)

    return Promise.resolve({
      data: {
        entityRelations: relations
      }
    })
  }

  static async generateReport (params) {
    return window.axios({
      url: process.env.VUE_APP_DEMO_REPORT_GQL_ENDPOINT,
      params: params,
      method: 'GET',
      responseType: 'blob'
    })
  }
}

// UTILS

function makeCSF (value, possibleValues, reference, type, required, name, title) {
  return {
    errors: null,
    possibleValues: possibleValues,
    spec: {
      causer: false,
      editable: false,
      name: name,
      reference: reference,
      required: required,
      title: title
    },
    type: type,
    value: value
  }
}

function getContractStateFields (c) {
  return [
    makeCSF(80, null, null, 'IntegerField', false, 'archive_after_days', 'Переход в архив через N дней после завершения действия договора'),
    makeCSF(c.archive_at, null, null, 'DatetimeField', false, 'archive_at', 'Дата перехода договора в архив'),
    makeCSF(c.auto_archive, null, null, 'BooleanField', false, 'auto_archive', 'Автоматическое архивирование договора'),
    makeCSF(c.close_at, null, null, 'DatetimeField', true, 'close_at', 'Дата окончания действия договора'),
    makeCSF(c.company_id, null, true, 'IntegerField', false, 'company_id', 'Компания организатор'),
    makeCSF(c.counteragent_company_id, null, true, 'IntegerField', true, 'counteragent_company_id', 'Контрагент'),
    makeCSF(c.counteragent_user_id, null, true, 'IntegerField', false, 'counteragent_user_id', 'Менеджер контрагента'),
    makeCSF(c.currency_id, null, true, 'IntegerField', true, 'currency_id', 'Валюта'),
    makeCSF(c.delivery_address, null, null, 'StringField', true, 'delivery_address', 'Адрес доставки'),
    makeCSF(c.has_real_contract, null, null, 'BooleanField', false, 'has_real_contract', 'Есть бумажный договор'),
    makeCSF(c.need_agreement, null, null, 'BooleanField', false, 'need_agreement', 'Требуется согласование'),
    makeCSF(c.number, null, null, 'StringField', false, 'number', 'Номер договора'),
    makeCSF(c.start_at, null, null, 'DatetimeField', true, 'start_at', 'Дата начала действия договора'),
    makeCSF(c.status_code, ['active', 'deleted'], null, 'StringField', false, 'status_code', 'Статус'),
    makeCSF(c.subsidiary_id, null, true, 'IntegerField', false, 'subsidiary_id', 'Филиал организатора'),
    makeCSF(c.tender_id, null, true, 'IntegerField', false, 'tender_id', 'Тендер'),
    makeCSF(c.title, null, null, 'StringField', false, 'title', 'Наименование договора'),
    makeCSF(c.vat, null, null, 'DecimalField', false, 'vat', 'НДС')
  ]
}

function getOrderStateFields (o) {
  return [
    makeCSF(o.company_id, null, true, 'IntegerField', false, 'company_id', 'Компания организатор'),
    makeCSF(o.contract_id, null, true, 'IntegerField', false, 'contract_id', 'Договор'),
    makeCSF(o.counteragent_company_id, null, true, 'IntegerField', false, 'counteragent_company_id', 'Контрагент'),
    makeCSF(o.counteragent_user_id, null, true, 'IntegerField', false, 'counteragent_user_id', 'Менеджер контрагента'),
    makeCSF(o.currency_id, null, true, 'IntegerField', false, 'currency_id', 'Валюта'),
    makeCSF(o.delivery_address, null, null, 'StringField', false, 'delivery_address', 'Адрес доставки'),
    makeCSF(o.delivery_at, null, null, 'DatetimeField', false, 'delivery_at', 'Дата поставки'),
    makeCSF(o.number, null, null, 'StringField', false, 'number', 'Номер Заказа'),
    makeCSF(o.start_at, null, null, 'DatetimeField', false, 'start_at', 'Дата начала действия заказа'),
    makeCSF(o.status_code, ['draft', 'approved', 'deleted'], null, 'StringField', false, 'status_code', 'Статус'),
    makeCSF(o.subsidiary_id, null, true, 'IntegerField', false, 'subsidiary_id', 'Филиал организатора'),
    makeCSF(o.title, null, null, 'StringField', false, 'title', 'Наименование заказа'),
    makeCSF(o.vat, null, null, 'DecimalField', false, 'vat', 'НДС')
  ]
}

function getLogs (entities, entitiesStatuses) {
  return entities.reduce((logsAcc, entity) => {
    return entity.status_history.reduce((entityLogsAcc, statusItem) => {
      const statusUser = users.find(u => u.id === statusItem.user_id)
      const prevStatusItem = entityLogsAcc.slice(-1)[0]
      entityLogsAcc.push({
        // чтобы один и тот же метод можно было использовать для договоров и заказов
        contractId: entity.id,
        orderId: entity.id,
        // чтобы один и тот же метод можно было использовать для договоров и заказов
        contractTitle: entity.title,
        orderTitle: entity.title,
        // чтобы один и тот же метод можно было использовать для договоров и заказов
        contractOwnerId: entity.company_id,
        orderOwnerId: entity.company_id,

        companyId: statusUser.companyId,
        companyTitle: statusUser.companyTitle,
        userId: statusUser.id,
        userName: statusUser.fullName,
        prevStatus: prevStatusItem ? prevStatusItem.newStatus : null,
        newStatus: entitiesStatuses.find(s => s.id === statusItem.status_id).code,
        insertedAt: statusItem.datetime
      })
      return entityLogsAcc
    }, logsAcc)
  }, [])
}
